import { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import { useCurrency } from '../context/CurrencyContext';
import { useProfile } from '../context/ProfileContext';
import { useUserPlan } from '../context/UserPlanContext';
import { useT } from '../i18n/useT';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { COIN_MAP } from '../utils/coingecko';
import { fmt, fmtC } from '../utils/format';
import { useCountUp } from '../hooks/useCountUp';
import { calcPortfolio, calcAssetCashIn, calcAssetCashOut } from '../utils/portfolioCalc';
import { buildPortfolioValueSeries } from '../utils/portfolioHistory';
import { buildReport } from '../utils/reportEngine';
import { buildFifoPool } from '../utils/fifo';
import { useTransferMatcher } from '../hooks/useTransferMatcher';
import { useHistoricalPrices } from '../hooks/useHistoricalPrices';
import { F, MONO_FEAT, mr, DUR, EASE } from '../styles/theme';

import CoinImage from '../components/CoinImage';
import Private from '../components/Private';
import SectionHeader from '../components/red/SectionHeader';
import DeltaPill from '../components/red/DeltaPill';
import { Icon, Icons } from '../components/red/Icons';
import AdBanner from '../components/red/AdBanner';
import SmartCheckMini from '../components/red/SmartCheckMini';
import { AnimatedArea, Sparkline } from '../components/red/Motion';
import { usePress } from '../components/red/Press';
import TimeRail from '../components/red/TimeRail';
import HeroCardChart from '../components/red/HeroCardChart';
import { listAlerts, updateAlert } from '../services/alertsApi';
import SwipeAction from '../components/SwipeAction';
import TxRow from '../components/TxRow';
import { buildTxView } from '../utils/txView';
import { useAuth } from '../context/AuthContext';
import MemberCard from '../components/red/MemberCard';
import EmbossPct from '../components/red/EmbossPct';
import { getOrAssignMemberNumber, formatMemberNo } from '../services/memberNumber';

const RED = '#E4544A';

// ── 2 schmale iOS-Slider-Werbungen (unter der Hero) ──────────────────
function AdSliders() {
  const { T } = useTheme();
  const { t } = useT();
  const ads = [
    { title: t('ad_slot_title'), sub: t('ad_slot_sub') },
    { title: t('ad_slot2_title'), sub: t('ad_slot2_sub') },
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, margin: '6px 0 10px' }}>
      {ads.map((a, i) => (
        <div key={i} style={{
          position: 'relative', borderRadius: 16, padding: '14px 16px',
          background: T.surfaceHi || T.surface, border: `1px solid ${T.line}`,
          boxShadow: T.cardInset, minHeight: 62, overflow: 'hidden',
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
        }}>
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: T.mute }}>{t('ad_label')}</div>
          <div style={{ fontFamily: F.display, fontSize: 14.5, fontWeight: 700, letterSpacing: '-0.02em', color: T.text, marginTop: 3 }}>{a.title}</div>
          <div style={{ position: 'absolute', right: 14, bottom: 12, display: 'flex', gap: 4 }}>
            {[0, 1, 2].map(d => (
              <span key={d} style={{ width: 5, height: 5, borderRadius: 3, background: d === 0 ? T.sub : T.line }} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Sicherheits-Zeile (Calm-Mock, unten) ─────────────────────────────
function SafetyNote() {
  const { T } = useTheme();
  const { t } = useT();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 18, color: T.sub, fontSize: 13.5, lineHeight: 1.4 }}>
      <span style={{
        width: 30, height: 30, borderRadius: 15, flexShrink: 0,
        background: T.surfaceHi || T.surface, border: `1px solid ${T.line}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <span style={{ width: 8, height: 8, borderRadius: 4, background: RED }} />
      </span>
      <span><b style={{ color: T.text, fontWeight: 700 }}>{t('dash_safety_strong')}</b> {t('dash_safety_rest')}</span>
    </div>
  );
}

// Empty-State für den Verlaufs-Chart. Statt gestrichelter „unfertig"-Box eine
// ruhige Komposition: weiche Fläche + gespenstisches Chart-Linien-Glyph als
// Metapher + Display-Headline. Kein Glow, kein roter Tint (feedback_no_pills…).
function HistoryEmpty({ height, compact }) {
  const { T } = useTheme();
  const { t } = useT();
  return (
    <div style={{
      height,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      gap: compact ? 10 : 16,
      textAlign: 'center', padding: compact ? '0 18px' : '0 32px',
      overflow: 'hidden',
    }}>
      <svg width={compact ? 96 : 128} height={compact ? 30 : 40}
        viewBox="0 0 128 40" aria-hidden="true" style={{ opacity: 0.7 }}>
        <line x1="0" y1="32" x2="128" y2="32" stroke={T.line}
          strokeWidth="1" strokeDasharray="1 6" />
        <path d="M0 30 C 22 26, 34 13, 52 18 S 92 31, 128 9"
          fill="none" stroke={T.mute} strokeWidth="1.6"
          strokeLinecap="round" strokeLinejoin="round" opacity="0.65" />
      </svg>
      <div style={{ maxWidth: compact ? 280 : 330 }}>
        {!compact && (
          <div style={{
            fontFamily: F.display, fontSize: 16, fontWeight: 700,
            letterSpacing: '-0.02em', color: T.sub, marginBottom: 6,
          }}>{t('dash_history_empty_title')}</div>
        )}
        <div style={{
          fontFamily: F.family, fontSize: compact ? 12 : 12.5,
          color: T.mute, lineHeight: 1.5,
        }}>{t('dash_history_empty')}</div>
      </div>
    </div>
  );
}

// CoinGecko-ID → Symbol. Alerts speichern nur `coinId` (z.B. "ethereum"),
// nie `coinSym` — siehe AlertWizard. Ohne Rück-Mapping landet die ID als
// vermeintliches Symbol in CoinImage ("ETHEREUM" → kein Logo, generischer
// Kreis) und als Label. Reverse-Map einmalig aus COIN_MAP aufbauen.
const ID_TO_SYM = Object.values(COIN_MAP).reduce((m, c) => {
  if (c.id) m[c.id] = c.sym;
  return m;
}, {});

// CoinGecko-`days` je Verlaufs-Range → steuert die Auflösung der geholten Reihe
// (1=5-Min, 2–90=stündlich, >90=täglich). Modul-Konstante → stabil über Renders.
const RANGE_FETCH_DAYS = { '24h': 1, '7T': 7, '30T': 30, '90T': 90, 'Max': 365 };

// Range-Umschalter-Knopf im Desktop-Hero („Bühne"). Eigene Komponente, damit
// jeder Knopf seinen eigenen usePress-Hook bekommt (Hooks dürfen nicht in .map).
function StageRangeButton({ active, label, onClick, txt, mutedC }) {
  const { pressTransform, pressHandlers } = usePress({ scale: 0.96 });
  return (
    <button onClick={onClick} {...pressHandlers} style={{
      background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0',
      fontFamily: F.family, fontSize: 13, letterSpacing: '-0.01em',
      fontWeight: active ? 700 : 600,
      color: active ? txt : mutedC,
      WebkitTapHighlightColor: 'transparent',
      transform: pressTransform,
      transition: `color ${DUR.base}ms ease, transform ${DUR.press}ms ${EASE}`,
    }}>{label}</button>
  );
}

// ── HeroCard ────────────────────────────────────────────────────────
function HeroCard({ tv, ti, gv, txs, holdings, marketData, onOpenMarkets, onOpenAlerts, onTabChange, onOpenDataCheckFix, tier = 'free', memberSince = '2026', memberNo = '——' }) {
  const { T, isDark } = useTheme();
  const { isWide } = useBreakpoint();
  const { convert, symbol, locale } = useCurrency();
  const { t } = useT();
  // gv kommt bereits konsistent (bewerteter Marktwert − Kosten) vom Parent —
  // NICHT (tv − ti) neu rechnen, sonst zählt unbewerteter Marktwert als Gewinn.
  const animated = useCountUp(convert(tv), 950);
  const { name } = useProfile();
  const _hr = new Date().getHours();
  const greeting = `${t(_hr < 11 ? 'greet_morning' : _hr < 18 ? 'greet_day' : 'greet_evening')}${name ? ', ' + name : ''}.`;
  const timeGreeting = t(_hr < 11 ? 'greet_morning' : _hr < 18 ? 'greet_day' : 'greet_evening');
  const sourceCount = useMemo(() => {
    const s = new Set();
    for (const tx of (txs || [])) {
      const src = tx.exchange || tx.platform || tx.wallet;
      if (src) s.add(String(src).toLowerCase());
    }
    return s.size;
  }, [txs]);
  // 24h portfolio change — weighted by holding value
  const chg24h = useMemo(() => {
    if (!holdings?.length || !marketData) return 0;
    let totalNow = 0, total24hAgo = 0;
    for (const h of holdings) {
      const meta = COIN_MAP[h.sym];
      const md = marketData?.[meta?.id];
      if (!md) continue;
      const price = md.price ?? 0;
      const chg = md.chg ?? 0;
      const valueNow = (h.menge || 0) * price;
      const value24hAgo = valueNow / (1 + chg / 100);
      totalNow += valueNow;
      total24hAgo += value24hAgo;
    }
    if (total24hAgo <= 0) return 0;
    return ((totalNow - total24hAgo) / total24hAgo) * 100;
  }, [holdings, marketData]);
  const positions = holdings?.filter(h => (h.value || 0) > 0).length || 0;
  const totalInvested = holdings?.reduce((s, h) => s + (h.invested || 0), 0) || 0;

  // ── Verlaufs-Chart (Desktop Hero rechte Spalte) ──
  // ECHTE Portfolio-Wertreihe: gehaltene Menge pro Coin (aus txs rekonstruiert)
  // × historischer Preis (CoinGecko market_chart via Proxy). Pro Range wird in
  // PASSENDER GRANULARITÄT neu geholt (24h=5-Min, 7T/30T/90T=stündlich,
  // Max=täglich) — sonst wäre 24h nur eine Gerade aus 2 Tagespunkten.
  // Siehe utils/portfolioHistory.js. KEINE synthetischen Daten mehr.
  const [heroRange, setHeroRange] = useState('90T');
  // Scrub-Wert beim Wischen über die In-Karten-Kurve; null = aktueller Stand.
  const [scrubVal, setScrubVal] = useState(null);
  // Content-responsive Vermögenszahl (Mobile-Hero): verfügbare Breite messen →
  // Schrift skalieren, damit auch 7-stellige Kundenbeträge nie überlaufen.
  const valBoxRef = useRef(null);
  const valMeasureRef = useRef(null);
  const [valBoxW, setValBoxW] = useState(0);
  useEffect(() => {
    const el = valBoxRef.current;
    if (!el) return;
    const update = () => setValBoxW(el.clientWidth);
    update();
    let ro; try { ro = new ResizeObserver(update); ro.observe(el); } catch (_) {}
    window.addEventListener('resize', update);
    return () => { try { ro && ro.disconnect(); } catch (_) {} window.removeEventListener('resize', update); };
  }, []);
  // Mobile hat keinen Range-Switcher → immer Max-Verlauf. Desktop folgt der Auswahl.
  // Range gilt auf Desktop UND Mobile (Mobile-Hero hat jetzt ebenfalls den
  // inline-Segmented-Switcher statt fix „Max").
  const effectiveRange = heroRange;
  // „Max" = tatsächliche Historie seit der ERSTEN Transaktion (nicht fix 365).
  // Wichtig: `days` steuert auch die CoinGecko-Granularität (≤90 = stündlich,
  // >90 = täglich). Mit fixen 365 lieferte „Max" daher immer nur eine grobe
  // Tagesreihe — bei kurzer Historie weniger Punkte als 90T. Jetzt: echte Spanne,
  // gedeckelt auf 365 (CoinGecko-Limit) → kurze Historien laufen stündlich.
  const maxDays = useMemo(() => {
    let earliest = Infinity;
    for (const tx of (txs || [])) {
      const ms = new Date(tx.datum || tx.date || '').getTime();
      if (Number.isFinite(ms) && ms < earliest) earliest = ms;
    }
    if (!Number.isFinite(earliest)) return 365;
    const days = Math.ceil((Date.now() - earliest) / 86400000) + 1;
    return Math.max(1, Math.min(365, days));
  }, [txs]);
  const [history, setHistory] = useState({ values: [], ts: [], loading: true });
  useEffect(() => {
    let cancelled = false;
    if (!txs || txs.length === 0) {
      setHistory({ values: [], ts: [], loading: false });
      return;
    }
    setHistory(h => ({ ...h, loading: true }));
    // Bucket-Strategie gegen CoinGecko-Verbrauch: 7T/30T/90T teilen sich EINE
    // 90-Tage-Reihe (stündlich) und werden nur clientseitig auf ihr Fenster
    // geschnitten → beim Umschalten zwischen diesen Ranges 0 neue Calls (market_chart
    // ist pro Coin nicht batchbar). 24h und Max bleiben eigene Buckets.
    const fetchDays = effectiveRange === 'Max' ? maxDays
      : effectiveRange === '24h' ? 1
      : 90;
    const windowDays = effectiveRange === 'Max' ? maxDays : (RANGE_FETCH_DAYS[effectiveRange] || 90);
    // EUR-Basis: die Reihe zeigt nur die Form (keine Achsenwerte), daher reicht
    // eine konsistente Basiswährung — der Wert-Header nutzt weiterhin convert().
    buildPortfolioValueSeries({ txs, days: fetchDays, windowDays, currency: 'eur' })
      .then(res => { if (!cancelled) setHistory({ values: res.values, ts: res.ts, loading: false }); })
      .catch(() => { if (!cancelled) setHistory({ values: [], ts: [], loading: false }); });
    return () => { cancelled = true; };
  }, [txs, effectiveRange, maxDays]);

  const heroSlice = history.values;
  // Variante B: Linie richtungs-getönt — grün wenn der Zeitraum im Plus endet, sonst rot.
  const chartColor = (heroSlice.length >= 2 && heroSlice[heroSlice.length - 1] >= heroSlice[0])
    ? (T.gain || T.green) : (T.loss || T.red);

  // X-Achsen-Labels — aus den TATSÄCHLICHEN Timestamps abgeleitet (4 gleichmäßig
  // verteilte Stützpunkte). Granularität: 24h → Uhrzeit, kurze Ranges Tag(+Monat),
  // lange nur Monat/Jahr.
  const axisLabels = useMemo(() => {
    const ts = history.ts;
    if (!ts || ts.length < 2) return [];
    const MONTHS_DE = ['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'];
    const MONTHS_EN = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const MONTHS = String(locale).startsWith('de') ? MONTHS_DE : MONTHS_EN;
    const spanMs = ts[ts.length - 1] - ts[0];
    const DAY = 86400000;
    const fmtLabel = (ms) => {
      const d = new Date(ms);
      if (spanMs <= 2 * DAY)   return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; // 24h → Uhrzeit
      if (spanMs <= 14 * DAY)  return `${d.getDate()}.`;                          // 7T → Tag
      if (spanMs <= 100 * DAY) return `${d.getDate()}. ${MONTHS[d.getMonth()]}`;  // 30T/90T → Tag + Monat
      if (spanMs <= 400 * DAY) return MONTHS[d.getMonth()];                       // ~1 Jahr → Monat
      return String(d.getFullYear());                                            // länger → Jahr
    };
    const picks = [...new Set([0,
      Math.round((ts.length - 1) / 3),
      Math.round((2 * (ts.length - 1)) / 3),
      ts.length - 1,
    ])];
    return picks.map(i => fmtLabel(ts[i]));
  }, [history.ts, locale]);

  // ── Desktop-Hero = „Bühne" (Werk III): dunkle Bühne, perspektivische
  //    MemberCard mit langem Schatten, Museums-Label-Typo links, Verlauf als
  //    ferner Horizont, Range im App-Text-Stil. NUR Webapp/Desktop — der
  //    Mobile-Hero (!isWide, unten) bleibt 1:1 die Karte-als-Hero mit
  //    In-Card-Graph + TimeRail. Bewusst immer dunkel (Bühnen-Palette).
  if (isWide) {
    const gain = gv >= 0;
    const dark = isDark;
    // Tag-/Nachtmodus-Paletten der Bühne. Nacht = schwarze Bühne (cream Text).
    // Tag = warme „Studio"-Bühne (Taupe-Grau, tiefer als der Seiten-BG #E9E9E7
    // und tiefer als die Creme-Karten → free/premium heben sich per Schatten ab,
    // vip-dunkel kontrastiert stark). Text in Ink.
    const stageBg = dark
      ? 'radial-gradient(120% 95% at 72% -12%, #2a2b33 0%, #16171c 44%, #0c0c0f 100%)'
      : 'radial-gradient(130% 100% at 72% -15%, #F4F2EE 0%, #ECE9E4 55%, #E1DED7 100%)';
    const txt = dark ? '#F4F1EA' : '#1A1817';
    const mutedC = dark ? '#8d877d' : '#7A746B';
    const metaL = dark ? '#6f6a61' : '#8E887E';
    const metaV = dark ? '#E7E3DC' : '#26231F';
    const subC = dark ? '#a59f95' : '#6A655D';
    const gainC = dark ? '#6FBF98' : '#3E9E6E';
    const lossC = dark ? '#E2685C' : '#C8453B';
    const poolBg = dark ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.55)';
    const floorBg = dark
      ? 'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.025) 60%, rgba(255,255,255,0.05) 100%)'
      : 'linear-gradient(180deg, rgba(40,32,24,0) 0%, rgba(40,32,24,0.035) 60%, rgba(40,32,24,0.07) 100%)';
    const stageShadow = dark ? '0 28px 70px rgba(0,0,0,0.5)' : '0 22px 54px rgba(40,30,20,0.15)';
    const stageBorder = dark ? 'none' : '1px solid rgba(40,32,24,0.10)';
    const cardCast = dark ? 'rgba(0,0,0,0.55)' : 'rgba(40,30,20,0.28)';
    const ranges = ['24h', '7T', '30T', '90T', 'Max'];
    return (
      <div style={{ position: 'relative', marginBottom: 8 }}>
        <div style={{
          position: 'relative', overflow: 'hidden', borderRadius: 22,
          background: stageBg, boxShadow: stageShadow, border: stageBorder,
          padding: '44px 50px', minHeight: 430, color: txt,
        }}>
          {/* Licht-Pool + Boden (neutral, kein farbiges Glühen) */}
          <div style={{ position: 'absolute', right: '6%', top: '-10%', width: 560, height: 560, borderRadius: '50%', pointerEvents: 'none', background: `radial-gradient(closest-side, ${poolBg}, rgba(255,255,255,0) 70%)` }} />
          <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: '46%', pointerEvents: 'none', background: floorBg }} />

          {/* Verlauf als Horizont (full-bleed unten) — INTERAKTIV wie mobil:
             Wischen/Maus über die Kurve → die Vermögens-Zahl folgt (scrubVal),
             Loslassen → zurück auf aktuell. Koralle-Jetzt-Punkt + Crosshair.
             Liegt über dem Inhalt-Layer (zIndex 2); der Inhalt ist pointerEvents:none,
             damit der Scrub auf voller Breite greift (Range-Buttons bleiben klickbar). */}
          {heroSlice.length >= 2 && (
            <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 168, zIndex: 2 }}>
              <HeroCardChart
                key={heroRange}
                values={heroSlice}
                line={gainC}
                baseTone={dark ? 'rgba(255,255,255,0.14)' : 'rgba(40,32,24,0.14)'}
                baselineValue={totalInvested}
                height={168}
                onScrub={setScrubVal}
                responsive
                fillOpacity={dark ? 0.14 : 0.08}
              />
            </div>
          )}

          {/* Range-Umschalter — App-Text-Stil (aktiv hervorgehoben/fett) */}
          <div style={{ position: 'absolute', top: 22, right: 26, zIndex: 4, display: 'flex', gap: 20 }}>
            {ranges.map(r => (
              <StageRangeButton key={r} active={heroRange === r} label={r}
                onClick={() => setHeroRange(r)} txt={txt} mutedC={mutedC} />
            ))}
          </div>

          {/* Inhalt: Begrüßung + Wert links · Karte rechts. pointerEvents:none →
             der Scrub auf der Kurve darunter greift auf voller Breite. */}
          <div style={{ position: 'relative', zIndex: 3, pointerEvents: 'none', display: 'grid', gridTemplateColumns: '1fr auto', gap: 40, alignItems: 'center' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: F.family, fontSize: 17, fontWeight: 500, color: subC }}>{greeting}</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 16, whiteSpace: 'nowrap', minWidth: 0 }}>
                <span style={{ fontFamily: F.num, fontVariantNumeric: 'tabular-nums', fontWeight: 800, fontSize: 'clamp(56px, 6vw, 96px)', letterSpacing: '-0.045em', lineHeight: 0.92, color: txt }}>
                  {tv > 0 ? <Private chars={6}>{(scrubVal != null ? scrubVal : animated).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Private> : '–'}
                </span>
                <span style={{ fontFamily: F.num, fontVariantNumeric: 'tabular-nums', fontWeight: 500, fontSize: 'clamp(26px, 3vw, 44px)', color: mutedC }}>{symbol}</span>
              </div>
              {tv > 0 && ti > 0 && (
                <div style={{ marginTop: 16, fontFamily: F.family, fontSize: 16, fontWeight: 500, color: subC, maxWidth: 540 }}>
                  {t('dash_vs_prefix')}{' '}
                  <span style={{ color: gain ? gainC : lossC, fontWeight: 800 }}>
                    {gain ? '↗ ' : '↘ '}<Private>{`${Math.round(convert(Math.abs(gv))).toLocaleString(locale)} ${symbol}`}</Private> {gain ? t('dash_more') : t('dash_less')}
                  </span>
                  {t('dash_vs_suffix')}
                </div>
              )}
              <div style={{ display: 'flex', gap: 40, marginTop: 30, flexWrap: 'wrap' }}>
                {[
                  { l: t('dash_stat_values'), v: String(positions) },
                  { l: t('dash_stat_sources'), v: String(sourceCount) },
                  { l: t('dash_stat_today'), v: `${chg24h >= 0 ? '+' : '−'}${Math.abs(chg24h).toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`, c: chg24h >= 0 ? gainC : lossC },
                ].map((m, i) => (
                  <div key={i} style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: metaL }}>{m.l}</div>
                    <div style={{ fontFamily: F.num, fontVariantNumeric: 'tabular-nums', fontSize: 17, fontWeight: 800, color: m.c || metaV, marginTop: 5 }}>{m.v}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* MemberCard perspektivisch (nach links geschwenkt) + langer Schatten */}
            <div style={{ position: 'relative', width: 330, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 220 }}>
              <div style={{ position: 'absolute', bottom: 18, left: '50%', width: 300, height: 46, transform: 'translateX(-42%) skewX(-30deg)', background: `radial-gradient(closest-side, ${cardCast}, rgba(0,0,0,0) 75%)`, filter: 'blur(8px)', zIndex: 0 }} />
              <div style={{ position: 'relative', zIndex: 1, transform: 'perspective(1100px) rotateY(-19deg) rotateX(5deg)', transformOrigin: 'center' }}>
                <MemberCard tier={tier} memberSince={memberSince} no={memberNo} width={300} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'relative',
        marginBottom: 8,
        padding: isWide ? '4px 0 0' : '8px 2px 0',
        color: T.text,
      }}
    >
      {/* Calm-Hero (Mock web-overview-rail-calm): EINE ruhige Spalte. */}
      <div style={{ position: 'relative', display: 'flex', flexDirection: 'column' }}>

      {/* Kopf: links Begrüßung/Wert, rechts (Desktop) die Mitgliedskarte im Weißraum.
          Karte sitzt am Anfang der rechten Hälfte (ab Mittellinie) → näher zur Mitte
          statt am rechten Rand. */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 32 }}>
      <div style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>

      {/* Begrüßung + (Mobile) Glocke rechts auf EINER Zeile (nativer Kopf,
          kein separater Header). Desktop unverändert: nur die Begrüßung. */}
      <div style={{ display: 'flex', alignItems: isWide ? 'center' : 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: isWide ? 18 : 12 }}>
        {!isWide ? (
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 500, color: T.sub }}>{timeGreeting}</div>
            {name && (
              <div style={{ fontFamily: F.display, fontSize: 27, fontWeight: 800, letterSpacing: '-0.03em', color: T.text, marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {name}
              </div>
            )}
          </div>
        ) : (
          <div style={{ fontSize: 18, fontWeight: 500, color: T.sub }}>{greeting}</div>
        )}
        {!isWide && tv > 0 && (
          /* Karte ganz oben rechts (perspektivisch gekippt, trägt das Tier).
             Die Glocke ist ins Profil gewandert (neben den Stift) → ruhiger Kopf;
             das Vermögen rückt darunter zentriert in die Mitte, der Hero atmet. */
          <div style={{
            flexShrink: 0,
            transform: 'perspective(820px) rotateY(-18deg) rotateX(5deg)', transformOrigin: 'right center',
            filter: T.isDark ? 'drop-shadow(0 14px 22px rgba(0,0,0,0.5))' : 'drop-shadow(0 12px 20px rgba(60,40,25,0.22))',
          }}>
            <MemberCard tier={tier} memberSince={memberSince} no={memberNo} width={132} />
          </div>
        )}
      </div>
      {!isWide ? (
        /* ── Mobile-Bühne (Werk A): Hintergrund = App-Theme; die Mitgliedskarte
           wird zum kleinen, perspektivisch gekippten Objekt oben rechts (trägt
           das Tier wie auf dem Desktop-Hero); das Vermögen ist der Held; der
           Verlauf läuft als randloser Horizont. Die linke Wisch-Zone bleibt frei
           (transparenter Strip über der Chart-Kante) → der native Pager-Swipe→
           Markt kollidiert nicht mehr mit dem Scrub. */
        (() => {
          const cardGain = T.gain || T.green;
          const cardLoss = T.loss || T.red;
          const dispNum = scrubVal != null ? scrubVal : animated;
          const numStr = tv > 0 ? dispNum.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '–';
          // Content-responsiv: Schrift so skalieren, dass jeder Kundenbetrag passt
          // (7-stellige Vermögen würden bei fixem 56px überlaufen). Gemessen wird die
          // Ziel-Länge (nicht der animierte Count-up) via Canvas → kein Jitter, kein
          // Layout-Thrash. Reserve für €-Symbol + Gap eingerechnet.
          const MAX_FONT = 56, MIN_FONT = 28;
          let numFont = MAX_FONT;
          if (tv > 0 && valBoxW > 0 && typeof document !== 'undefined') {
            const cv = valMeasureRef.current || (valMeasureRef.current = document.createElement('canvas'));
            const ctx = cv.getContext('2d');
            ctx.font = `800 ${MAX_FONT}px ${F.num}`;
            const sizeStr = (scrubVal != null ? scrubVal : convert(tv)).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            const tw = ctx.measureText(sizeStr).width;
            const avail = valBoxW * 0.78;
            if (tw > avail) numFont = Math.max(MIN_FONT, Math.floor(MAX_FONT * avail / tw));
          }
          const symFont = Math.max(16, Math.round(numFont * 0.48));
          return (
          <div style={{ position: 'relative', marginTop: 26, marginBottom: 4 }}>
            {/* Vermögen — der Held: groß und ZENTRIERT (Karte sitzt oben rechts,
               Glocke ist ins Profil gewandert → der Wert rückt in die Mitte). Die
               Zahl folgt beim Wischen dem Finger; fontSize content-responsiv
               (numFont) → kein Überlauf bei großen Beträgen. */}
            <div style={{ position: 'relative', textAlign: 'center' }}>
              <div ref={valBoxRef} style={{ display: 'flex', justifyContent: 'center', alignItems: 'baseline', gap: 5, whiteSpace: 'nowrap', maxWidth: '100%', overflow: 'hidden' }}>
                <span style={{ fontFamily: F.num, fontVariantNumeric: 'tabular-nums', fontWeight: 800, fontSize: numFont, letterSpacing: '-0.05em', color: T.text }}>
                  {tv > 0 ? <Private chars={6}>{numStr}</Private> : '–'}
                </span>
                <span style={{ fontFamily: F.num, fontVariantNumeric: 'tabular-nums', fontWeight: 500, fontSize: symFont, color: T.sub }}>{symbol}</span>
              </div>
              {tv > 0 && ti > 0 && (
                <div style={{ display: 'inline-flex', alignItems: 'baseline', gap: 7, marginTop: 14, fontFamily: F.num, fontVariantNumeric: 'tabular-nums' }}>
                  <span style={{ fontSize: 15, fontWeight: 800, color: gv >= 0 ? cardGain : cardLoss }}>
                    {gv >= 0 ? '↗' : '↘'} <Private>{`${gv >= 0 ? '+' : '−'}${Math.round(convert(Math.abs(gv))).toLocaleString(locale)} ${symbol}`}</Private>
                  </span>
                  <span style={{ fontSize: 15, fontWeight: 500, color: T.sub }}>seit Einzahlung</span>
                </div>
              )}
            </div>

            {/* Verlauf als Horizont (full-bleed). Transparenter Strip über der linken
               Kante = freie Wisch-Zone (≥ EDGE_ZONE=48 aus LateralPager): der Chart
               bekommt dort kein pointerdown → kein Scrub, der native Pager-Swipe→
               Markt greift sauber. Chart NICHT mit data-noswipe markieren (das
               würde den Pager ganz abschalten). */}
            {heroSlice.length >= 2 ? (
              <div style={{ position: 'relative', width: 'calc(100% + 44px)', marginLeft: -22, marginTop: 28 }}>
                <HeroCardChart
                  values={heroSlice}
                  line={cardGain}
                  baseTone={T.isDark ? 'rgba(255,255,255,0.12)' : 'rgba(27,27,30,0.14)'}
                  baselineValue={totalInvested}
                  height={104}
                  onScrub={setScrubVal}
                />
                <div aria-hidden="true" style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 72, zIndex: 2 }} />
              </div>
            ) : <div style={{ height: 14 }} />}
          </div>
          );
        })()
      ) : (
        <>
          <div style={{ fontSize: 18, fontWeight: 600, color: T.sub, marginBottom: 8 }}>
            {t('dash_your_wealth')}
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 14, marginTop: 6, whiteSpace: 'nowrap', minWidth: 0 }}>
            <span style={{ fontFamily: F.num, fontVariantNumeric: 'tabular-nums', fontWeight: 700, fontSize: 'clamp(52px, 6.2vw, 92px)', letterSpacing: '-0.045em', lineHeight: 0.95, color: T.text }}>
              {tv > 0 ? <Private chars={6}>{animated.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Private> : '–'}
            </span>
            <span style={{ fontFamily: F.num, fontVariantNumeric: 'tabular-nums', fontWeight: 500, fontSize: 'clamp(52px, 6.2vw, 92px)', letterSpacing: '-0.045em', lineHeight: 0.95, color: T.sub }}>{symbol}</span>
          </div>
          {tv > 0 && ti > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14, marginBottom: 2 }}>
              <svg viewBox="0 0 20 20" width={24} height={24} fill="none" aria-hidden="true"
                style={{ color: gv >= 0 ? (T.gain || T.green) : (T.loss || T.red), flexShrink: 0,
                         transform: gv >= 0 ? 'none' : 'rotate(90deg) scaleY(-1)' }}>
                <path d="M4 16L16 4M8 4h8v8" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <div style={{ fontFamily: F.family, fontSize: 19, fontWeight: 500, color: T.sub, lineHeight: 1.45, maxWidth: 540 }}>
                {t('dash_vs_prefix')}{' '}
                <span style={{ color: gv >= 0 ? (T.gain || T.green) : (T.loss || T.red), fontWeight: 700 }}>
                  <Private>{`${Math.round(convert(Math.abs(gv))).toLocaleString(locale)} ${symbol}`}</Private> {gv >= 0 ? t('dash_more') : t('dash_less')}
                </span>
                {t('dash_vs_suffix')}
              </div>
            </div>
          )}
        </>
      )}

      </div>{/* /linke Kopf-Spalte */}
      {isWide && (
        <div style={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start', paddingTop: 8 }}>
          <MemberCard
            tier={tier} memberSince={memberSince} no={memberNo}
            width={300}
            style={{ flexShrink: 0 }}
          />
        </div>
      )}
      </div>{/* /Kopf-Row */}

      {/* Verlauf — Desktop: Range oben rechts. Mobile (Ledger-Stil): Chart
         randlos/full-bleed (bricht aus der Seiten-Polsterung), Range UNTER dem
         Chart, zentriert. */}
      {tv > 0 && (
        <div style={{ marginTop: isWide ? 24 : 2 }}>
          {/* Desktop: Range oben rechts + großer Chart. Mobile: Chart ist IN der
             Karte (interaktiv) — hier nur der TimeRail-Regler darunter. */}
          {isWide && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: 4 }}>
              {/* Gleicher Slide-Thumb-Regler wie auf Mobile (TimeRail), oben rechts
                  in fixer Breite — ersetzt das alte Segmented. */}
              <div style={{ width: 320 }}>
                <TimeRail value={heroRange} onChange={setHeroRange} options={['24h', '7T', '30T', '90T', 'Max']} T={T} />
              </div>
            </div>
          )}
          {isWide && (history.loading ? (
            <div style={{ height: 190, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.mute, fontSize: 12 }}>{t('dash_history_loading')}</div>
          ) : heroSlice.length >= 2 ? (
            <AnimatedArea key={heroRange} data={heroSlice} height={190} color={chartColor} months={axisLabels} app={false} />
          ) : (
            <HistoryEmpty height={190} compact={false} />
          ))}
          {!isWide && (
            <TimeRail value={heroRange} onChange={setHeroRange} options={['24h', '7T', '30T', '90T', 'Max']} T={T} />
          )}
        </div>
      )}

      {/* Leise Kennzahlen-Zeile (Mock): eingezahlt · heute · Werte · Quellen */}
      {tv > 0 && (
        <div style={
          isWide
            ? { display: 'flex', gap: 44, flexWrap: 'wrap', marginTop: 22, paddingTop: 18, borderTop: `1px solid ${T.line}` }
            : { display: 'flex', marginTop: 16, paddingTop: 16, borderTop: `1px solid ${T.line}` }
        }>
          {[
            { v: <Private>{`${Math.round(convert(totalInvested)).toLocaleString(locale)} ${symbol}`}</Private>, k: t('dash_stat_invested'), c: T.text },
            { v: `${chg24h >= 0 ? '+' : '−'}${Math.abs(chg24h).toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`, k: t('dash_stat_today'), c: chg24h >= 0 ? (T.gain || T.green) : (T.loss || T.red) },
            { v: String(positions), k: t('dash_stat_values'), c: T.text },
            { v: String(sourceCount), k: t('dash_stat_sources'), c: T.text },
          ].slice(0, isWide ? 4 : 2).map((s, i, arr) => (
            <div key={i} style={isWide ? {} : { flex: 1, minWidth: 0, textAlign: 'center', padding: '0 2px', borderRight: i < arr.length - 1 ? `1px solid ${T.line}` : 'none' }}>
              <div style={{ ...MONO_FEAT, fontSize: isWide ? 19 : 15, fontWeight: 700, letterSpacing: '-0.02em', color: s.c, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.v}</div>
              <div style={{ fontFamily: F.family, fontSize: isWide ? 12 : 10.5, fontWeight: 500, color: T.sub, marginTop: 4, lineHeight: 1.2 }}>{s.k}</div>
            </div>
          ))}
        </div>
      )}

      </div>{/* /Calm-Hero */}
    </div>
  );
}

// ── AssetRow ────────────────────────────────────────────────────────
function AssetRow({ holding, marketData, onClick, index, pct }) {
  const { T } = useTheme();
  const { convert, symbol, locale } = useCurrency();
  const { pressTransform, pressHandlers } = usePress({ scale: 0.97 });
  const meta = COIN_MAP[holding.sym];
  const md = marketData?.[meta?.id];
  const price = md?.price ?? 0;
  const chg = md?.chg ?? 0;
  const value = holding.menge * price;
  return (
    <div
      onClick={onClick}
      className="fade-up mo-row"
      {...pressHandlers}
      style={{
        animationDelay: `${index * 0.04}s`,
        background: T.surface,
        border: `1px solid ${T.line}`,
        borderRadius: mr(16),
        padding: 14,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        cursor: 'pointer',
        boxShadow: 'none',
        color: T.text,
        transform: pressTransform,
        transition: `transform ${DUR.press}ms ${EASE}`,
      }}
    >
      <span className="mo-lead" style={{ display: 'inline-flex', flexShrink: 0 }}>
        <CoinImage sym={holding.sym} marketData={marketData} size={40} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: F.display, fontWeight: 600, fontSize: 14, letterSpacing: '-0.02em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {meta?.name || holding.sym}
        </div>
        <div style={{ fontFamily: F.mono, fontSize: 11, color: T.sub, marginTop: 3 }}>
          <Private>{fmtC(holding.menge)}</Private> {holding.sym}
        </div>
      </div>
      {/* Vermögens-Anteil — geprägte Ziffer (EmbossPct), wie in der Webapp-
          Bestandsliste. Eigene Spalte zwischen Name und Wert. */}
      {pct != null && (
        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
          <EmbossPct value={pct} T={T} size={24}
            ariaLabel={`${pct < 1 ? '<1' : pct.toFixed(0)} % deines Vermögens`} />
        </div>
      )}
      <div style={{ textAlign: 'right', flexShrink: 0, minWidth: 92 }}>
        <div style={{ fontFamily: F.display, fontWeight: 600, fontSize: 14, letterSpacing: '-0.02em' }}>
          <Private>{`${fmt(convert(value), locale)} ${symbol}`}</Private>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 3 }}>
          <DeltaPill value={chg} small plain />
        </div>
      </div>
    </div>
  );
}

// ── HoldingsTable — Editorial Bestände-Tabelle (Desktop) ─────────────
// #/Asset (CoinImage+Sym+Menge)/Kurs/7T-Spark/Wert+Δ/›  — Hairlines, Hover-Highlight
function HoldingsTable({ holdings, hidden = [], onToggleHide, marketData, onSelectAsset, T, accent, txs = [] }) {
  const { convert, symbol, locale } = useCurrency();
  const { t } = useT();
  const [showHidden, setShowHidden] = useState(false);
  // Verwaltungs-Modus (statt Hover-Augen-Icon): „Bearbeiten" schaltet die Liste
  // in einen iOS-artigen Edit-State — Linksklick-Details pausiert, je Zeile ein
  // klares −/+ zum Aus-/Einblenden, ausgeblendete Coins erscheinen inline.
  const [editMode, setEditMode] = useState(false);

  // Cash-Flow je Coin einmal vorberechnen (sonst O(Coins × TXs) pro Render).
  const cashflowBySym = useMemo(() => {
    const m = {};
    for (const h of [...holdings, ...hidden]) {
      if (m[h.sym]) continue;
      m[h.sym] = {
        cashIn: calcAssetCashIn(h.sym, txs),
        cashOut: calcAssetCashOut(h.sym, txs).cashOut,
      };
    }
    return m;
  }, [holdings, hidden, txs]);

  // Gesamtwert für „X % deines Vermögens" je Zeile (Calm-Mock).
  const totalVal = useMemo(() => holdings.reduce((s, h) => {
    const md = marketData?.[COIN_MAP[h.sym]?.id];
    return s + (h.menge || 0) * (md?.price || 0);
  }, 0), [holdings, marketData]);

  if (!holdings.length && !hidden.length) return null;

  const renderRow = (h, i, isHidden) => {
    const meta = COIN_MAP[h.sym];
    const md = marketData?.[meta?.id];
    const price = md?.price ?? 0;
    const chg = md?.chg ?? 0;
    const spark = md?.spark || null;
    const value = (h.menge || 0) * price;
    // Rendite = bereinigte Cash-Flow-Sicht (identisch zur red.-Analyse, Bild 2):
    // (Bestand heute + bereits rausgenommen) − eingezahlt, in % der Einzahlung.
    // Einzahlungen selbst sind keine Rendite — nur der Überschuss darüber zählt.
    // So ist je Coin klar ersichtlich, ob unterm Strich Geld gewonnen/verloren
    // wurde (inkl. realisierter Verkäufe), nicht nur der unrealisierte Anteil.
    // cashIn = 0 (reiner Transfer-/Airdrop-Zufluss ohne Einzahlung) → "—".
    const { cashIn = 0, cashOut = 0 } = cashflowBySym[h.sym] || {};
    const totalPerformance = (value + cashOut) - cashIn;
    const gainPct = cashIn > 1e-6
      ? (totalPerformance / cashIn) * 100
      : null;
    return (
      <HoldingsRow key={h.sym}
        idx={i} sym={h.sym} name={meta?.name || h.sym}
        menge={h.menge} price={price} chg={chg} spark={spark}
        value={value} gainPct={gainPct}
        gainAbs={gainPct == null ? null : totalPerformance}
        pct={totalVal > 0 ? (value / totalVal) * 100 : null}
        marketData={marketData}
        isHidden={isHidden}
        editMode={editMode}
        onToggleHide={onToggleHide}
        onClick={() => onSelectAsset({ sym: h.sym, coinId: meta?.id, name: meta?.name || h.sym })}
        T={T} accent={accent} convert={convert} symbol={symbol} locale={locale}
      />
    );
  };
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '18px 0 14px',
        borderBottom: `1px solid ${T.line}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
          <h3 style={{
            fontFamily: F.display, fontSize: 22, fontWeight: 700,
            letterSpacing: '-0.03em', margin: 0, color: T.text,
          }}>{t('dash_your_holdings')}</h3>
        </div>
        {/* „Bearbeiten" ganz rechts in der Bestand-Zeile — schaltet den Edit-State. */}
        <button
          onClick={() => setEditMode(e => !e)}
          aria-pressed={editMode}
          style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '4px 2px',
            fontFamily: F.family, fontSize: 13.5,
            fontWeight: editMode ? 700 : 600,
            color: editMode ? (T.signal || T.red) : T.sub,
            WebkitTapHighlightColor: 'transparent', transition: `color ${DUR.press}ms ease`,
          }}
          onMouseEnter={e => { if (!editMode) e.currentTarget.style.color = T.text; }}
          onMouseLeave={e => { if (!editMode) e.currentTarget.style.color = T.sub; }}
        >
          {editMode ? t('dash_done') : t('dash_edit')}
        </button>
      </div>
      {/* Spaltenköpfe entfallen — schlichte Zeilen wie im Calm-Mock. */}
      <div>
        {holdings.map((h, i) => renderRow(h, i, false))}
        {/* Im Edit-Modus erscheinen ausgeblendete Coins inline (gedimmt, mit „+"),
            damit man sie an Ort und Stelle zurückholen kann. */}
        {editMode && hidden.map((h, i) => renderRow(h, i, true))}
      </div>

      {/* Ausgeblendete Positionen — aufklappbar (nur außerhalb des Edit-Modus,
          sonst doppelt). */}
      {!editMode && hidden.length > 0 && (
        <div style={{ borderTop: `1px solid ${T.lineSoft || T.line}` }}>
          <button
            onClick={() => setShowHidden(s => !s)}
            aria-label={t('dash_n_hidden', { n: hidden.length })}
            title={t('dash_n_hidden', { n: hidden.length })}
            aria-expanded={showHidden}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '12px 16px', background: 'none', border: 'none', cursor: 'pointer',
              color: T.sub, WebkitTapHighlightColor: 'transparent',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = T.text; }}
            onMouseLeave={e => { e.currentTarget.style.color = T.sub; }}
          >
            <Icon d={Icons.chevD} size={18} stroke="currentColor" sw={2.4}
              style={{ transform: showHidden ? 'rotate(180deg)' : 'none', transition: `transform ${DUR.base}ms` }} />
          </button>
          {showHidden && (
            <div>
              {hidden.map((h, i) => renderRow(h, i, true))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function HoldingsRow({ sym, name, menge, spark, chg, value, gainPct, gainAbs, pct, marketData, onClick, isHidden, editMode, onToggleHide, T, convert, symbol, locale }) {
  const [hover, setHover] = useState(false);
  const { t } = useT();
  const { pressTransform, pressHandlers } = usePress({ scale: 0.96, disabled: editMode });
  const deltaColor = gainPct == null ? T.mute : (gainPct >= 0 ? (T.gain || T.green) : (T.loss || T.red));
  // Im Edit-Modus navigiert die Zeile nicht (Linksklick = Details ist pausiert);
  // Aktion läuft ausschließlich über den −/+ Knopf.
  const rowClick = editMode ? undefined : onClick;
  return (
    <div
      onClick={rowClick}
      className="mo-row"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      {...(editMode ? {} : pressHandlers)}
      style={{
        display: 'flex', alignItems: 'center', gap: 15,
        padding: '15px 2px', cursor: editMode ? 'default' : 'pointer',
        background: (!editMode && hover ? T.itemHover : 'transparent'),
        opacity: isHidden && !editMode ? 0.5 : (isHidden ? 0.55 : 1),
        borderTop: `1px solid ${T.lineSoft || T.line}`,
        transform: pressTransform,
        transition: `background ${DUR.fast}ms ease, opacity ${DUR.press}ms, transform ${DUR.press}ms ${EASE}`,
      }}
    >
      {/* Edit-Modus: führender −/+ Knopf (iOS-Listenmuster). − blendet aus,
          + holt einen ausgeblendeten Coin zurück. */}
      {editMode && onToggleHide && (
        <button
          onClick={(e) => { e.stopPropagation(); onToggleHide(sym); }}
          aria-label={isHidden ? t('dash_show') : t('dash_hide')}
          title={isHidden ? t('dash_show') : t('dash_hide')}
          style={{
            flexShrink: 0, width: 24, height: 24, borderRadius: '50%',
            display: 'grid', placeItems: 'center', cursor: 'pointer', border: 'none',
            color: '#fff', fontSize: 18, fontWeight: 700, lineHeight: 1, paddingBottom: 1,
            background: isHidden ? (T.green || '#4F9C73') : (T.signal || T.red),
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {isHidden ? '+' : '−'}
        </button>
      )}
      <span className="mo-lead" style={{ display: 'inline-flex', flexShrink: 0 }}>
        <CoinImage sym={sym} marketData={marketData} size={40} />
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{
          fontFamily: F.display, fontWeight: 700, fontSize: 16,
          letterSpacing: '-0.02em', color: T.text,
        }}>{name}</div>
        <div style={{ fontSize: 13, color: T.sub, marginTop: 3 }}>
          <span style={{ ...MONO_FEAT }}><Private>{fmtC(menge)}</Private> {sym}</span>
        </div>
      </div>
      {/* Vermögens-Anteil — eigene Spalte (geprägte Punkt-Zahl). marginLeft:auto
          ankert den rechten Cluster; feste Breiten ⇒ alle Anteile fluchten links
          vertikal untereinander. Kein „Allokation"-Label mehr. */}
      <div style={{ marginLeft: 'auto', width: 84, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
        {pct != null && (
          <EmbossPct value={pct} T={T} size={28}
            ariaLabel={`${pct < 1 ? '<1' : pct.toFixed(0)} % deines Vermögens`} />
        )}
      </div>
      <div style={{ width: 70, marginRight: 30, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexShrink: 0 }}>
        {spark && spark.length > 1 && (
          <Sparkline data={spark} color={chg >= 0 ? (T.gain || T.green) : (T.loss || T.red)} width={70} height={24} sw={1.6} />
        )}
      </div>
      <div style={{ textAlign: 'right', minWidth: 116 }}>
        <div style={{ ...MONO_FEAT, fontWeight: 700, fontSize: 16, color: T.text }}>
          <Private>{`${fmt(convert(value), locale)} ${symbol}`}</Private>
        </div>
        <div style={{ ...MONO_FEAT, fontSize: 13, fontWeight: 600, marginTop: 2, color: deltaColor }}>
          {gainPct == null
            ? '—'
            : <Private>{`${gainAbs >= 0 ? '+' : '−'}${fmt(convert(Math.abs(gainAbs)), locale)} ${symbol}`}</Private>}
        </div>
      </div>
    </div>
  );
}

// ── MiniAlertsCard — Right-Sidebar widget ────────────────────────────
function MiniAlertsCard({ marketData, onOpenAlerts, T, accent }) {
  const { t } = useT();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const list = await listAlerts();
        if (alive) setAlerts(list || []);
      } catch { /* ignore */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);
  // Erste 3 Alerts zeigen (aktiv ODER pausiert) — sonst würde ein gerade
  // pausierter Alert durch den Toggle sofort aus dem Widget verschwinden.
  const active = alerts.slice(0, 3);

  // Aktiv-Toggle direkt im Widget — pausiert/aktiviert ohne Umweg über den
  // Alerts-Screen. Optimistisches UI-Update, Fehler wird still zurückgerollt.
  const toggleAlert = async (a) => {
    const nextActive = a.active === false;
    setAlerts(prev => prev.map(x => x.id === a.id ? { ...x, active: nextActive } : x));
    try { await updateAlert(a.id, { active: nextActive }); }
    catch { setAlerts(prev => prev.map(x => x.id === a.id ? { ...x, active: !nextActive } : x)); }
  };

  return (
    <div style={{ paddingTop: 2 }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        padding: '18px 0 14px',
        borderBottom: `1px solid ${T.line}`,
        marginBottom: 14,
      }}>
        <h3 style={{
          fontFamily: F.display, fontSize: 18, fontWeight: 700,
          letterSpacing: '-0.02em', margin: 0, color: T.text,
        }}>{t('dash_active_alerts')}</h3>
        {onOpenAlerts && (
          <button onClick={onOpenAlerts} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: T.sub, fontSize: 12, fontWeight: 700, fontFamily: F.family,
          }}>{t('dash_all_arrow')}</button>
        )}
      </div>
      {loading ? (
        <div style={{ color: T.mute, fontSize: 12, fontFamily: F.family }}>{t('loading')}</div>
      ) : active.length === 0 ? (
        <div style={{
          color: T.sub, fontSize: 12.5, fontFamily: F.family,
          lineHeight: 1.5,
        }}>
          {t('dash_no_active_alerts')}{' '}
          <button onClick={onOpenAlerts} style={{
            background: 'none', border: 'none', padding: 0,
            color: accent, fontWeight: 700, cursor: 'pointer',
            fontFamily: F.family, fontSize: 12.5,
          }}>{t('dash_create_now')}</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {active.map((a, i) => {
            const coin = (a.coinSym || ID_TO_SYM[a.coinId] || a.coinId || '').toUpperCase();
            const isPortfolio = a.type === 'portfolio_value';
            const op = a.type === 'price_above' ? '≥'
                     : a.type === 'price_below' ? '≤'
                     : a.type === 'pct_change'  ? '±'
                     : a.type === 'holding_period' ? '·' : '≥';
            const tail = a.type === 'holding_period' ? t('dash_holding_period')
                       : a.type === 'pct_change' ? `${a.threshold ?? '?'} %`
                       : `${a.threshold ?? '?'} ${a.currency || 'EUR'}`;
            return (
              <div key={a.id || i} style={{
                display: 'flex', alignItems: 'center', gap: 11,
                padding: '10px 0',
                borderTop: i ? `1px solid ${T.lineSoft || T.line}` : 'none',
                opacity: a.active === false ? 0.5 : 1,
                transition: `opacity ${DUR.fast}ms`,
              }}>
                {isPortfolio
                  ? <div style={{
                      width: 26, height: 26, borderRadius: '50%',
                      border: `1px solid ${T.line}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: T.sub, fontFamily: F.display, fontSize: 13, fontWeight: 800,
                    }}>∑</div>
                  : <CoinImage sym={coin} marketData={marketData} size={26} />}
                <div style={{
                  flex: 1, minWidth: 0,
                  fontSize: 12.5, color: T.text, fontWeight: 600,
                  fontFamily: F.family,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {isPortfolio ? t('portfolio') : coin} {op} <span style={{ ...MONO_FEAT }}>{tail}</span>
                </div>
                <button
                  onClick={() => toggleAlert(a)}
                  title={a.active === false ? t('dash_activate') : t('dash_pause')}
                  aria-pressed={a.active !== false}
                  style={{
                    width: 36, height: 21, borderRadius: 999,
                    background: a.active !== false ? (T.gain || T.green) : T.surfaceHi,
                    border: `1px solid ${a.active !== false ? (T.gain || T.green) : T.line}`,
                    cursor: 'pointer', position: 'relative', flexShrink: 0,
                    padding: 0, transition: `background ${DUR.fast}ms, border-color ${DUR.fast}ms`,
                  }}
                >
                  <span style={{
                    position: 'absolute', top: 2,
                    left: a.active !== false ? 17 : 2,
                    width: 15, height: 15, borderRadius: '50%',
                    background: '#fff',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
                    transition: `left ${DUR.fast}ms`,
                  }} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── ActivityRow — dünner Wrapper um den geteilten TxRow (wie WalletRow im
// Transaktionen-Tab). KEINE eigene Anzeige-Logik mehr: „Letzte Aktivität" zieht
// sich 1:1 aus derselben Quelle (txView/TxRow) wie der Transaktionen-Tab und
// kann darum nie wieder davon abweichen (Income/Staking, Transfer-Flow,
// MoonPay→Ledger, abgeleitete Werte etc. kommen automatisch mit).
function ActivityRow({ tx, marketData, transferMap, derivedValues, onOpen }) {
  const { locale } = useCurrency();
  const view = useMemo(
    () => buildTxView(tx, { transferMap, derivedValues, marketData, locale }),
    [tx, transferMap, derivedValues, marketData, locale]
  );
  return (
    <TxRow
      variant="card"
      view={view}
      marketData={marketData}
      flat
      rowRadius={3}
      rowShadow="none"
      onClick={onOpen ? () => onOpen(tx) : undefined}
    />
  );
}

// ── View-All Button (matches section header tone) ───────────────────
function ViewAllButton({ onClick, label }) {
  const { T } = useTheme();
  return (
    <button
      onClick={onClick}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        color: T.text,
        opacity: 0.62,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        cursor: 'pointer',
        fontFamily: F.family,
      }}
    >
      {label}
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true" style={{ display: 'block' }}>
        <path d="M2 5h6M5.5 2.5 8 5l-2.5 2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

// ── Top Mover strip ─────────────────────────────────────────────────
// variant: 'scroll' (mobile, horizontal) | 'list' (desktop, vertical)
export function TopMoverStrip({ marketData, onOpenMarkets, onSelectAsset, variant = 'scroll', hideHeader = false, hideViewAll = false }) {
  const { T } = useTheme();
  const { isWide } = useBreakpoint();
  const { convert, symbol, locale } = useCurrency();
  const { t } = useT();
  const [topCoins, setTopCoins] = useState([]);
  useEffect(() => {
    let alive = true;
    const cur = 'eur';
    (async () => {
      try {
        const _base = typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.() ? 'https://app.redcrypto.de' : '';
        const proxyUrl = `${_base}/api/coin-markets?currency=${cur}&top=250&sparkline=false`;
        let res = await fetch(proxyUrl);
        if (res.status === 404) {
          const cgUrl = `https://api.coingecko.com/api/v3/coins/markets?vs_currency=${cur}&order=market_cap_desc&per_page=250&page=1&sparkline=false&price_change_percentage=24h`;
          res = await fetch(cgUrl);
        }
        if (!res.ok) return;
        const arr = await res.json();
        if (!alive || !Array.isArray(arr)) return;
        setTopCoins(arr);
      } catch {}
    })();
    return () => { alive = false; };
  }, []);
  const items = useMemo(() => {
    return topCoins
      .map(c => {
        const chg = c.price_change_percentage_24h;
        if (typeof chg !== 'number' || !Number.isFinite(chg)) return null;
        return {
          sym: (c.symbol || '').toUpperCase(),
          meta: { id: c.id, name: c.name },
          chg,
          price: c.current_price,
          image: c.image,
        };
      })
      .filter(Boolean)
      .sort((a, b) => Math.abs(b.chg) - Math.abs(a.chg))
      .slice(0, 6);
  }, [topCoins]);
  const liveData = useMemo(() => {
    const d = { ...(marketData || {}) };
    topCoins.forEach(c => {
      d[c.id] = { price: c.current_price, chg: c.price_change_percentage_24h ?? 0, image: c.image };
    });
    return d;
  }, [marketData, topCoins]);
  if (!items.length) return null;
  const isList = variant === 'list';
  // Modern: gestaffelter Karten-Einzug (pop-in) statt Block-Slide. Hover/Tap
  // via framer für konsistente Federung.
  const containerVar = { hidden: {}, show: { transition: { staggerChildren: 0.06, delayChildren: 0.04 } } };
  const cardVar = {
    hidden: { opacity: 0, y: 18, scale: 0.94 },
    show: { opacity: 1, y: 0, scale: 1, transition: { duration: DUR.entrance / 1000, ease: [0.16, 1, 0.3, 1] } },
  };
  return (
    <div style={{ marginBottom: 12 }}>
      {!hideHeader && (
        <SectionHeader
          title={t('top_mover')}
          action={hideViewAll ? null : <ViewAllButton onClick={onOpenMarkets} label={t('view_all')} />}
        />
      )}
      <motion.div
        className={isList ? '' : 'hide-scrollbar'}
        variants={containerVar}
        initial="hidden"
        animate="show"
        style={isList
          ? { display: 'flex', flexDirection: 'column', gap: 8, padding: '0 2px' }
          : isWide
            ? { display: 'flex', gap: 12, padding: '2px 1px 6px' }
            : { display: 'flex', gap: 10, overflowX: 'auto', padding: '0 18px 4px' }
        }
      >
        {items.map(h => {
          const md = liveData?.[h.meta?.id];
          const price = md?.price ?? h.price ?? 0;
          return (
            <motion.div
              key={h.sym}
              variants={cardVar}
              whileHover={{ y: -4 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onSelectAsset({ sym: h.sym, coinId: h.meta?.id, name: h.meta?.name || h.sym, price, chg: h.chg, image: h.image })}
              style={isList ? {
                background: T.isDark ? T.surface : T.surface2,
                border: `1px solid ${T.line}`,
                borderRadius: mr(16),
                padding: '10px 12px',
                cursor: 'pointer',
                color: T.text,
                display: 'grid',
                gridTemplateColumns: '28px 1fr auto auto',
                alignItems: 'center',
                gap: 12,
              } : {
                // Desktop: Karten füllen die Reihe gleichmäßig (flex:1) und
                // bekommen Höhe + space-between, damit die bestehenden Infos die
                // Fläche füllen. Mobile: feste Breite + Scroll, kompakt.
                flex: isWide ? 1 : '0 0 auto',
                minWidth: 0,
                width: isWide ? 'auto' : 158,
                minHeight: isWide ? 178 : undefined,
                background: T.isDark ? T.surface : T.surface2,
                border: `1px solid ${T.line}`,
                borderRadius: mr(16),
                padding: isWide ? '14px 20px 20px' : 14,
                cursor: 'pointer',
                color: T.text,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: isWide ? 'space-between' : 'flex-start',
              }}
            >
              {isList ? (
                <>
                  <CoinImage sym={h.sym} src={h.image} marketData={liveData} size={26} />
                  <div style={{
                    fontFamily: F.display, fontSize: 13.5, fontWeight: 600,
                    color: T.text, letterSpacing: '-0.02em',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>{h.meta?.name || h.sym}</div>
                  <div style={{ fontFamily: F.mono, fontSize: 13, fontWeight: 600, color: T.text, letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>
                    {fmt(convert(price), locale)} <span style={{ opacity: 0.5, fontWeight: 600 }}>{symbol}</span>
                  </div>
                  <div style={{ fontFamily: F.family, fontSize: 12, fontWeight: 600, color: h.chg >= 0 ? T.green : T.red, letterSpacing: '-0.01em', whiteSpace: 'nowrap', minWidth: 56, textAlign: 'right' }}>
                    {h.chg >= 0 ? '+' : ''}{h.chg.toFixed(2)}%
                  </div>
                </>
              ) : isWide ? (
              /* Desktop: Logo + Pfeil oben, darunter Name, Preis, %·24h. */
              <>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <CoinImage sym={h.sym} src={h.image} marketData={liveData} size={40} />
                  <svg width="22" height="22" viewBox="0 0 16 16" fill="none" style={{ display: 'block', flexShrink: 0 }}>
                    {h.chg >= 0 ? (
                      <>
                        <path d="M4.5 11.5L11.5 4.5" stroke={T.green} strokeWidth="2" strokeLinecap="round" />
                        <path d="M6 4.5h5.5V10" stroke={T.green} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                      </>
                    ) : (
                      <>
                        <path d="M4.5 4.5L11.5 11.5" stroke={T.red} strokeWidth="2" strokeLinecap="round" />
                        <path d="M6 11.5h5.5V6" stroke={T.red} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                      </>
                    )}
                  </svg>
                </div>
                <div>
                  <div style={{
                    fontFamily: F.display, fontSize: 16, fontWeight: 700,
                    color: T.text, letterSpacing: '-0.02em',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>{h.meta?.name || h.sym}</div>
                  {/* Preis auf eigener Zeile → kein Umbruch bei langen Kleinpreisen. */}
                  <div style={{
                    fontFamily: F.display, fontSize: 26, fontWeight: 700, color: T.text,
                    letterSpacing: '-0.04em', lineHeight: 1, marginTop: 12,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {fmt(convert(price), locale)}<span style={{ fontSize: 14, opacity: 0.5, fontWeight: 600, marginLeft: 3 }}>{symbol}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 7 }}>
                    <span style={{ fontFamily: F.family, fontSize: 14, fontWeight: 700, color: h.chg >= 0 ? T.green : T.red, letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>
                      {h.chg >= 0 ? '+' : ''}{h.chg.toFixed(2)}%
                    </span>
                    <span style={{ fontFamily: F.family, fontSize: 11, fontWeight: 500, color: T.mute, letterSpacing: '0.02em' }}>· 24h</span>
                  </div>
                </div>
              </>
              ) : (
              /* Mobile: kompakte Scroll-Karte. */
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <CoinImage sym={h.sym} src={h.image} marketData={liveData} size={28} />
                  <div style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="20" height="20" viewBox="0 0 16 16" fill="none" style={{ display: 'block' }}>
                      {h.chg >= 0 ? (
                        <>
                          <path d="M4.5 11.5L11.5 4.5" stroke={T.green} strokeWidth="2" strokeLinecap="round" />
                          <path d="M6 4.5h5.5V10" stroke={T.green} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                        </>
                      ) : (
                        <>
                          <path d="M4.5 4.5L11.5 11.5" stroke={T.red} strokeWidth="2" strokeLinecap="round" />
                          <path d="M6 11.5h5.5V6" stroke={T.red} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                        </>
                      )}
                    </svg>
                  </div>
                </div>
                <div style={{
                  fontFamily: F.display, fontSize: 13, fontWeight: 600,
                  color: T.text, letterSpacing: '-0.02em',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  marginBottom: 14,
                }}>{h.meta?.name || h.sym}</div>
                <div style={{ fontFamily: F.display, fontSize: 18, fontWeight: 700, color: T.text, letterSpacing: '-0.035em', lineHeight: 1 }}>
                  {fmt(convert(price), locale)}<span style={{ fontSize: 13, opacity: 0.5, fontWeight: 600, marginLeft: 3 }}>{symbol}</span>
                </div>
                <div style={{ fontFamily: F.family, fontSize: 11.5, fontWeight: 600, color: h.chg >= 0 ? T.green : T.red, letterSpacing: '-0.01em', marginTop: 4 }}>
                  {h.chg >= 0 ? '+' : ''}{h.chg.toFixed(2)}% <span style={{ color: T.mute, fontWeight: 500, marginLeft: 2 }}>· 24h</span>
                </div>
              </>
              )}
            </motion.div>
          );
        })}
      </motion.div>
    </div>
  );
}

// ── Main Dashboard ──────────────────────────────────────────────────
export default function Dashboard({ txs, marketData, onTabChange, onSelectAsset, onOpenMarkets, onOpenAlerts, onOpenDataCheckFix }) {
  const { T } = useTheme();
  const { t } = useT();
  const { isWide } = useBreakpoint();
  const { plan } = useUserPlan();
  const { user } = useAuth();

  // MemberCard-Daten: Tier aus Plan, „Mitglied seit"-Jahr aus dem Konto-
  // Erstelldatum (also automatisch 2027 etc.), Nummer einmalig fortlaufend vergeben.
  const memberTier = plan === 'vip' ? 'vip' : plan === 'premium' ? 'premium' : 'free';
  const memberSince = useMemo(() => {
    try {
      const ts = user?.metadata?.creationTime;
      if (ts) return String(new Date(ts).getFullYear());
    } catch { /* ignore */ }
    return String(new Date().getFullYear());
  }, [user]);
  const [memberNo, setMemberNo] = useState('——');
  useEffect(() => {
    let alive = true;
    if (!user?.uid) { setMemberNo('——'); return undefined; }
    getOrAssignMemberNumber(user.uid).then(n => { if (alive) setMemberNo(formatMemberNo(n)); });
    return () => { alive = false; };
  }, [user]);

  const transferMap = useTransferMatcher(txs);
  const derivedValues = useHistoricalPrices(txs, { currency: 'eur' });
  const [showHidden, setShowHidden] = useState(false);
  const [hiddenAssets, setHiddenAssets] = useState(() => {
    try { return JSON.parse(localStorage.getItem('red_hidden_assets') || '[]'); }
    catch { return []; }
  });

  const toggleHide = (sym) => {
    const next = hiddenAssets.includes(sym)
      ? hiddenAssets.filter(s => s !== sym)
      : [...hiddenAssets, sym];
    setHiddenAssets(next);
    localStorage.setItem('red_hidden_assets', JSON.stringify(next));
    window.dispatchEvent(new CustomEvent('red:hidden_changed', { detail: next }));
  };

  useEffect(() => {
    const sync = (e) => {
      const next = Array.isArray(e?.detail)
        ? e.detail
        : (() => { try { return JSON.parse(localStorage.getItem('red_hidden_assets') || '[]'); } catch { return []; } })();
      setHiddenAssets(next);
    };
    window.addEventListener('red:hidden_changed', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('red:hidden_changed', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  // FIFO einmal bauen und an beide Konsumenten weiterreichen — sonst laeuft
  // buildFifoPool zweimal pro Render (calcPortfolio + buildReport intern).
  const fifoResult = useMemo(() => buildFifoPool(txs), [txs]);
  const portfolio = useMemo(
    () => Object.values(calcPortfolio(txs, { prebuiltFifo: fifoResult })),
    [txs, fifoResult]
  );
  // Single Source of Truth: identisch zur Berichte-Seite und zum PDF/Excel-Export.
  // year='all' summiert ueber alle Jahre (Dashboard-Lifetime-Sicht).
  const realizedGV = useMemo(
    () => buildReport({ txs, year: 'all', prebuiltFifo: fifoResult }).realizedPnL,
    [txs, fifoResult]
  );

  // enrich with current price for sorting/display
  const enriched = useMemo(() => {
    return portfolio.map(p => {
      const meta = COIN_MAP[p.sym];
      const price = marketData?.[meta?.id]?.price ?? 0;
      return {
        ...p, price,
        value: p.menge * price,
        // Marktwert des Anteils mit bekannter Kostenbasis — Basis der Rendite-%
        pricedValue: (p.mengePriced ?? p.menge) * price,
      };
    }).sort((a, b) => b.value - a.value);
  }, [portfolio, marketData]);

  const visible = enriched.filter(p => !hiddenAssets.includes(p.sym));
  const hidden = enriched.filter(p => hiddenAssets.includes(p.sym));

  const { tv, ti, tvPriced } = useMemo(() => {
    let tv = 0, ti = 0, tvPriced = 0;
    visible.forEach(p => { tv += p.value; ti += p.invested; tvPriced += (p.pricedValue || 0); });
    return { tv, ti, tvPriced };
  }, [visible]);
  // Unrealisierte Rendite konsistent: Marktwert des BEWERTETEN Bestands minus
  // dessen Anschaffungskosten. tv (Gesamtwert inkl. Lots ohne EK) bleibt für die
  // Wertanzeige, fließt aber NICHT in die Rendite — sonst zählt unbewerteter
  // Marktwert (Dust/Transfers ohne EK) als reiner Gewinn.
  const gv = tvPriced - ti;
  // Alle Positionen als lange Row (wie HBAR) — keine 2-up-Featured-Cards mehr.
  const rest = visible;

  const recentTxs = useMemo(() => {
    // Matched Transfer-Paare als EINE Zeile zeigen: die einzahlung-Seite ausfiltern,
    // die auszahlung-Seite rendert From→To inkl. Partner (siehe ActivityRow).
    const filtered = txs.filter(tx => {
      if (tx.typ !== 'einzahlung') return true;
      const id = tx._docId || String(tx.id);
      const info = transferMap.get(id);
      const matched = info?.matchStatus === 'matched' || !!tx.linkedTxId;
      return !matched;
    });
    return [...filtered].sort((a, b) => {
      const da = a.effectiveTimestamp?.slice(0, 10) || a.datum || '';
      const db = b.effectiveTimestamp?.slice(0, 10) || b.datum || '';
      if (da !== db) return db.localeCompare(da);
      const ta = a.effectiveTimestamp ? a.effectiveTimestamp.slice(11, 19) : (a.zeit || '00:00:00');
      const tb = b.effectiveTimestamp ? b.effectiveTimestamp.slice(11, 19) : (b.zeit || '00:00:00');
      if (ta !== tb) return tb.localeCompare(ta);
      return 0;
    }).slice(0, 5);
  }, [txs, transferMap]);

  // ── Sektions-Bausteine — Mobile + Desktop teilen die gleichen Children ──
  // Auf Desktop ist der äußere Padding bereits von DesktopShell gesetzt, deshalb
  // schalten wir die horizontalen Paddings pro Block weg.

  const heroSection = (
    <div style={{ padding: isWide ? 0 : '8px 18px 0' }}>
      <HeroCard tv={tv} ti={ti} gv={gv + realizedGV * 0} txs={txs} holdings={visible} marketData={marketData} onOpenMarkets={onOpenMarkets} onOpenAlerts={onOpenAlerts} onTabChange={onTabChange} onOpenDataCheckFix={onOpenDataCheckFix} tier={memberTier} memberSince={memberSince} memberNo={memberNo} />
      {/* Werbeplatz vorerst AUS (Mock-Treue, User-Wunsch). Einnahmen-Slot —
          zum Reaktivieren `false &&` entfernen. */}
      {false && !isWide && <AdBanner />}
    </div>
  );

  const assetsSection = (
    <>
      <SectionHeader
        title={t('your_assets')}
        action={
          // „Alle ansehen" führt zu den Transaktionen — auf Mobile redundant
          // (Tab existiert ohnehin), daher nur Desktop.
          isWide && enriched.length > 2 ? (
            <ViewAllButton onClick={() => onTabChange('wallet')} label={t('view_all')} />
          ) : null
        }
      />

      {enriched.length === 0 ? (
        <div style={{ margin: isWide ? '0 0 16px' : '0 18px 16px', padding: '36px 20px', borderRadius: 18, background: T.surface, border: `1px solid ${T.line}`, textAlign: 'center', color: T.sub }}>
          <svg width="56" height="56" viewBox="0 0 60 60" fill="none" style={{ display: 'block', margin: '0 auto 12px' }} aria-hidden="true">
            <rect x="10" y="10" width="17" height="17" rx="4.5" stroke={T.line} strokeWidth="2" />
            <rect x="33" y="10" width="17" height="17" rx="4.5" stroke={T.line} strokeWidth="2" />
            <rect x="10" y="33" width="17" height="17" rx="4.5" stroke={T.line} strokeWidth="2" />
            <rect x="33" y="33" width="17" height="17" rx="4.5" fill="#E4544A" />
            <line x1="41.5" y1="38" x2="41.5" y2="45" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
            <line x1="38" y1="41.5" x2="45" y2="41.5" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
          </svg>
          {t('dash_empty_coins_1')}<br/>{t('dash_empty_coins_2')}
        </div>
      ) : (
        <>
          {rest.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: isWide ? '0 0 16px' : '0 18px 16px' }}>
              {rest.map((h, i) => (
                <SwipeAction
                  key={h.sym}
                  radius={16}
                  actionLabel={t('dash_hide')}
                  actionIcon={Icons.eyeOff}
                  onAction={() => toggleHide(h.sym)}
                >
                  <AssetRow
                    holding={h}
                    marketData={marketData}
                    index={i}
                    pct={tv > 0 ? (h.value / tv) * 100 : null}
                    onClick={() => onSelectAsset({ sym: h.sym, coinId: COIN_MAP[h.sym]?.id, name: COIN_MAP[h.sym]?.name || h.sym })}
                  />
                </SwipeAction>
              ))}
            </div>
          )}
          {hidden.length > 0 && (
            <div style={{ padding: isWide ? '6px 0 16px' : '6px 18px 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: showHidden ? 10 : 0 }}>
                <button
                  onClick={() => setShowHidden(s => !s)}
                  aria-label={t('dash_n_hidden', { n: hidden.length })}
                  title={t('dash_n_hidden', { n: hidden.length })}
                  aria-expanded={showHidden}
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    padding: '6px 12px', borderRadius: 999,
                    background: 'transparent', border: 'none',
                    color: T.sub, cursor: 'pointer',
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  <Icon d={Icons.chevD} size={18} stroke={T.sub} sw={2.4} style={{ transform: showHidden ? 'rotate(180deg)' : 'none', transition: `transform ${DUR.base}ms` }} />
                </button>
              </div>
              {showHidden && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {hidden.map((h, i) => (
                    <div key={h.sym} style={{ opacity: 0.6, transition: `opacity ${DUR.base}ms` }}>
                      <SwipeAction
                        radius={16}
                        actionLabel={t('dash_show')}
                        actionIcon={Icons.eye}
                        actionColor="#37805C"
                        onAction={() => toggleHide(h.sym)}
                      >
                        <AssetRow
                          holding={h}
                          marketData={marketData}
                          index={i}
                          pct={tv > 0 ? (h.value / tv) * 100 : null}
                          onClick={() => onSelectAsset({ sym: h.sym, coinId: COIN_MAP[h.sym]?.id, name: COIN_MAP[h.sym]?.name || h.sym })}
                        />
                      </SwipeAction>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </>
  );

  const topMoversSection = onOpenMarkets ? (
    <TopMoverStrip
      marketData={marketData}
      onOpenMarkets={onOpenMarkets}
      onSelectAsset={onSelectAsset}
      variant={isWide ? 'list' : 'scroll'}
    />
  ) : null;

  // Activity-Card nach source/red-screens-dashboard.jsx blocks.activity.el:
  //   Card pad=0, Header: chapter "03" (Space Grotesk 12px 700 red)
  //   + h3 "Letzte Aktivität" (Bricolage 19px 800 -0.03em)
  //   + "Alle ›" Link rechts, dann Activity-Reihen mit lineSoft Trennern
  const activitySection = isWide ? (
    <div style={{ height: '100%' }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '18px 0 14px',
        borderBottom: `1px solid ${T.line}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <h3 style={{
            fontFamily: F.display, fontSize: 19, fontWeight: 700,
            letterSpacing: '-0.03em', margin: 0, color: T.text,
          }}>{t('recent_activity')}</h3>
        </div>
      </div>
      {txs.length === 0 ? (
        <div style={{ padding: '20px 16px', textAlign: 'center', color: T.sub, fontSize: 13 }}>
          {t('dash_no_transactions')}
        </div>
      ) : (
        recentTxs.map((tx) => (
          <ActivityRow
            key={tx.id}
            tx={tx}
            marketData={marketData}
            transferMap={transferMap}
            derivedValues={derivedValues}
            onOpen={() => onTabChange('wallet')}
          />
        ))
      )}
    </div>
  ) : (
    <>
      <SectionHeader title={t('recent_activity')} />
      <div style={{ margin: '0 18px', borderRadius: mr(16), background: T.surface, border: `1px solid ${T.line}`, boxShadow: `${T.cardInset}, ${T.cardShadow}`, overflow: 'hidden' }}>
        {txs.length === 0 ? (
          <div style={{ padding: '20px 16px', textAlign: 'center', color: T.sub, fontSize: 13 }}>
            {t('dash_no_transactions')}
          </div>
        ) : (
          recentTxs.map((tx) => (
            <ActivityRow
              key={tx.id}
              tx={tx}
              marketData={marketData}
              transferMap={transferMap}
              derivedValues={derivedValues}
              onOpen={() => onTabChange('wallet')}
            />
          ))
        )}
      </div>
    </>
  );

  // ── Desktop / Tablet: editoriales Magazin-Layout nach Handoff ──────
  //   1. LiveTicker  (Markt · Live)
  //   2. Hero (full-width, 2-Spalten intern)
  //   3. Figures (full-width, 4 Spalten)
  //   4. rw-main: Holdings | (Allocation + SmartCheck + Alerts)
  //   5. rw-cols-2: Activity | Markets
  if (isWide) {
    const holdingsBlock = (visible.length > 0 || hidden.length > 0) ? (
      <HoldingsTable
        holdings={visible}
        hidden={hidden}
        onToggleHide={toggleHide}
        marketData={marketData}
        onSelectAsset={onSelectAsset}
        txs={txs}
        T={T} accent={RED}
      />
    ) : assetsSection;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {heroSection}
        {/* Werbe-/Sponsor-Plätze entfernt — wirken ohne echte Partner wie
           ungenutzte Werbefläche. Wiederaufnahme mit echten Sponsoren (Mock-
           Richtungen in brand/web-sponsor-*.html). AdSliders bleibt ungenutzt. */}
        {holdingsBlock}
        {/* Untere Reihe (Calm-Mock): links Letzte Aktivität (halbe Breite),
            rechts Smart-Check + Alarme in der anderen Hälfte. */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 44,
          alignItems: 'start',
          marginTop: 12,
        }}>
          <div style={{ minWidth: 0 }}>
            {activitySection}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 28, minWidth: 0 }}>
            <SmartCheckMini txs={txs} onOpenSmartCheck={() => onTabChange('berichte')} />
            <MiniAlertsCard
              marketData={marketData}
              onOpenAlerts={() => onTabChange('alerts')}
              T={T} accent={RED}
            />
          </div>
        </div>
        <SafetyNote />
      </div>
    );
  }

  // ── Mobile (heutige Stapelung) ────────────────────────────────────
  return (
    <div style={{ paddingBottom: 100 }}>
      {heroSection}
      {assetsSection}
      {topMoversSection}
      {activitySection}
    </div>
  );
}
